<script setup>
import { ref, computed, watch } from "vue";
import { useBookingStore } from "@stores/BookingStore";
import CourseSelection from "@components/CourseSelection.vue";
import { formatPrice, applyDiscount } from "@app/helpers";

// Define Props
const props = defineProps({
  patient: Number,
  type: String,
  opened: {
    type: Boolean,
    default: false,
  },
  clinic: {
    type: Object,
    required: true,
  },
  vaccinations: {
    type: Array,
    required: false,
  },
  consultationDetails: {
    // New prop for consultation breakdown
    type: Object,
    required: false,
  },
  pricing: {
    type: Object,
    default: () => ({}), // Made optional with a default empty object
  },
  serviceType: {
    type: String,
    default: "vaccinations",
  },
});

// Reactive Variables
const bookingStore = useBookingStore();
const isOpen = ref(props.opened);

// Watch for external changes to 'opened' prop if needed, e.g., if parent controls it after mount
watch(
  () => props.opened,
  (newValue) => {
    isOpen.value = newValue;
  }
);

// Methods/Functions
const toggleState = () => {
  isOpen.value = !isOpen.value;
};

// Computed Properties
const hasDiscount = computed(() => {
  return Number(props.clinic.booster_max_discount) > 0;
});

const consultationItemsForDisplay = computed(() => {
  if (
    props.serviceType === "consultations" &&
    props.consultationDetails &&
    props.consultationDetails.breakdown
  ) {
    const items = [];
    let patientCounter = 1; // Sequential counter across all patient types
    props.consultationDetails.breakdown.forEach((typeItem) => {
      if (typeItem.count > 0) {
        // Only display if there are patients of this type
        typeItem.calculatedPrices.forEach((price, index) => {
          items.push({
            id: `${typeItem.patientType}-${index}`, // Unique key for v-for
            name: `${typeItem.label} - Patient ${patientCounter}`,
            price: formatPrice(price),
          });
          patientCounter++; // Increment for next patient
        });
      }
    });
    return items;
  }
  return [];
});</script>

<template>
  <article class="px-20 sm:px-30 py-13 border border-grey-border rounded-3xl">
    <header class="flex justify-between items-center">
      <h2
        v-if="type !== 'consultation_summary'"
        class="font-medium text-grey capitalize">
        {{ type }} {{ patient + 1 }}
      </h2>

      <h2 v-else class="mb-20 font-medium text-grey capitalize">
        Consultation Breakdown
      </h2>

      <i
        v-if="!isOpen"
        class="text-20 cursor-pointer ri-arrow-down-s-line"
        @click="toggleState"></i>
      <i
        v-if="isOpen"
        class="text-20 cursor-pointer ri-arrow-up-s-line"
        @click="toggleState"></i>
    </header>

    <div class="max-xs:max-w-[320px] overflow-auto">
      <table
        v-if="serviceType === 'vaccinations'"
        v-show="isOpen"
        class="p-20 rounded-xl w-full text-left"
        cellpadding="10">
        <thead>
          <tr>
            <th class="w-[50%]"></th>
            <th class="w-[25%] font-normal">Dose<br /></th>
            <th class="w-[25%] font-normal">Price <br /></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(vacc, index) in vaccinations" :key="index">
            <td class="align-top" v-html="vacc.name"></td>
            <td class="align-top">
              {{ vacc.course === "full" ? vacc.dose : 1 }}
            </td>
            <td class="align-top">
              <div class="flex max-sm:flex-col items-center gap-2">
                <div class="flex items-center gap-2">
                  <template v-if="hasDiscount && vacc.course === 'full'">
                    <span class="line-through">
                      £{{ formatPrice(Number(vacc.price) * Number(vacc.dose)) }}
                    </span>
                    <span class="font-medium text-red-600">
                      £{{
                        formatPrice(
                          applyDiscount(
                            Number(vacc.price) * Number(vacc.dose),
                            clinic.booster_max_discount
                          )
                        )
                      }}
                    </span>
                  </template>
                  <span v-else class="font-medium">
                    £{{ formatPrice(vacc.price) }}
                  </span>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <table
        v-if="serviceType === 'consultations'"
        v-show="isOpen && consultationItemsForDisplay.length > 0"
        class="p-20 rounded-xl w-full text-left"
        cellpadding="10">
        <thead>
          <tr>
            <th class="w-[75%] font-normal">Patient</th>
            <th class="w-[25%] font-normal">Price <br /></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in consultationItemsForDisplay" :key="item.id">
            <td class="align-top">{{ item.name }}</td>
            <td class="font-medium align-top">£{{ item.price }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </article>
</template>

<style scoped lang="scss"></style>
